/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// credential-store.js — the ONE place a secret lives (Feature 260).
//
// The app ships no credentials and calls nothing on its own; an AI connection
// exists only because the user pasted their own API key. That key is the first
// secret this app has ever held, so it gets its own store rather than a corner
// of settings.json — because settings.json is plaintext, mode 0644, and is
// handed BACK to the renderer in full on every `settings:get`. A key stored
// there would be readable on disk and re-seeded into the Settings dialog on
// every open.
//
// Instead: Electron's `safeStorage`, which encrypts against the OS keychain
// (Keychain on macOS, libsecret/kwallet on Linux, DPAPI on Windows), written
// atomically through the same io.js primitives every other store uses.
//
// TWO RULES, both about not lying to the user:
//
//   1. If encryption is unavailable, REFUSE — never silently fall back to
//      plaintext. A user who was told their key is stored securely should not
//      have it sitting in a readable file because a Linux box had no keyring.
//   2. A stored key NEVER comes back out across the IPC bridge. `status()`
//      answers whether one is configured; only the main-process AI client
//      reads the value, and only to put it in an outgoing request header.
//
// And the header goes only where the key was MEANT for: each key is stored
// with the ORIGIN it was entered for (`origins`, beside the keys), and
// `keyFor` hands it out only for a request to that origin. The base URL of a
// request is the renderer's to say, so without this a compromised renderer
// could have main deliver the key to any https host it named.

const path = require("path");

const io = require("./io");

/** Provider ids we will store a key for — an allowlist, not free-form. */
const PROVIDERS = Object.freeze(["anthropic", "openai-compat"]);

class CredentialStore {
  /**
   * @param {string} dataDir - the app's userData directory.
   * @param {{isEncryptionAvailable:Function, encryptString:Function,
   *          decryptString:Function}} safeStorage - Electron's safeStorage
   *   (injected so this is testable without an Electron runtime).
   */
  constructor(dataDir, safeStorage) {
    this._file = path.join(dataDir, "credentials.json");
    this._safe = safeStorage ?? null;
  }

  /** Whether the OS can encrypt for us at all. */
  available() {
    try {
      return Boolean(this._safe?.isEncryptionAvailable?.());
    } catch {
      return false;
    }
  }

  _read() {
    const doc = io.readJSON(this._file);
    return doc && typeof doc === "object" && !Array.isArray(doc) ? doc : {};
  }

  /**
   * Store a provider's key, encrypted.
   *
   * @returns {{ok:true}|{ok:false, error:string}}
   */
  set(providerId, key, { origin = null } = {}) {
    if (!PROVIDERS.includes(providerId)) {
      return { ok: false, error: `Unknown provider "${providerId}".` };
    }
    if (typeof key !== "string" || !key.trim()) {
      return { ok: false, error: "The key is empty." };
    }
    if (!this.available()) {
      return {
        ok: false,
        error:
          "This system has no secure credential store available, so the key " +
          "cannot be saved. Set it again next session, or configure an OS " +
          "keyring.",
      };
    }
    let encrypted;
    try {
      encrypted = this._safe.encryptString(key.trim()).toString("base64");
    } catch (err) {
      return { ok: false, error: `Could not encrypt the key: ${err.message}` };
    }
    const doc = this._read();
    const origins = { ...this._origins(doc) };
    if (origin) origins[providerId] = origin;
    else delete origins[providerId];
    io.writeJSON(this._file, { ...doc, [providerId]: encrypted, origins });
    return { ok: true };
  }

  /**
   * The decrypted key, or null. MAIN-PROCESS ONLY — this value must never be
   * returned over an IPC channel; see the header note.
   *
   * @returns {string|null}
   */
  get(providerId) {
    const stored = this._read()[providerId];
    if (typeof stored !== "string" || !stored) return null;
    if (!this.available()) return null;
    try {
      const plain = this._safe.decryptString(Buffer.from(stored, "base64"));
      return typeof plain === "string" && plain ? plain : null;
    } catch {
      // A key encrypted under a keychain we can no longer open (a restored
      // machine, a changed login) is gone, not recoverable — say "not
      // configured" rather than throwing on every request.
      return null;
    }
  }

  /** Forget a provider's key (and the origin it was for). */
  clear(providerId) {
    const doc = this._read();
    const origins = { ...this._origins(doc) };
    if (!(providerId in doc) && !(providerId in origins)) return { ok: true };
    delete doc[providerId];
    delete origins[providerId];
    io.writeJSON(this._file, { ...doc, origins });
    return { ok: true };
  }

  _origins(doc = this._read()) {
    const o = doc.origins;
    return o && typeof o === "object" && !Array.isArray(o) ? o : {};
  }

  /** The origin a provider's key was entered for, or null (none recorded —
      a key stored before origins were). */
  originOf(providerId) {
    if (!PROVIDERS.includes(providerId)) return null;
    const origin = this._origins()[providerId];
    return typeof origin === "string" && origin ? origin : null;
  }

  /** Record the origin of a key stored before origins were. */
  bind(providerId, origin) {
    if (!PROVIDERS.includes(providerId) || !origin) return;
    const doc = this._read();
    if (!(providerId in doc)) return;
    io.writeJSON(this._file, {
      ...doc,
      origins: { ...this._origins(doc), [providerId]: origin },
    });
  }

  /**
   * The decrypted key for a request to `origin` — null when there is none,
   * or when it was entered for another origin. MAIN-PROCESS ONLY.
   * @returns {string|null}
   */
  keyFor(providerId, origin) {
    const bound = this.originOf(providerId);
    if (!bound || !origin || bound !== origin) return null;
    return this.get(providerId);
  }

  /**
   * What the renderer is allowed to know: whether a key is there, never what
   * it is.
   *
   * @returns {{configured:boolean, encryptionAvailable:boolean}}
   */
  status(providerId) {
    return {
      configured: Boolean(this.get(providerId)),
      encryptionAvailable: this.available(),
    };
  }
}

module.exports = { CredentialStore, PROVIDERS };
